import { useState } from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Typography,
  Button,
} from '@mui/material';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootStateType } from 'src/shared/services/redux/reducers';
import { fileUrl } from 'src/shared/services/utils/url';
import { FeedResponse } from 'src/shared/services/api/response/feedResponse';
import NewsModal from 'src/modules/news/components/newsModal';
import { Colors } from 'src/shared/components/styles';
import { isParentCompany } from 'src/shared/helpers/companyHelper';
import { toast } from 'react-toastify';
import { GetAdminPermission } from 'src/shared/helpers/permissionHelper';
import {
  createNewsContentParserOptions,
  normalizeDocumentLinksForRender,
} from 'src/shared/helpers/documentLinks';
import { DocumentAPI } from 'src/shared/services/api/documentAPI';

type FeedNewsItemType = {
  item: FeedResponse;
};

const FeedNewsItem = (props: FeedNewsItemType) => {
  const { item } = props;
  const { t } = useTranslation();
  const { user } = useSelector((state: RootStateType) => state.oidc);

  const [imageError, setImageError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const normalizedContent = normalizeDocumentLinksForRender(
    item?.newsItem?.content ?? '',
  );

  const handleShare = () => {
    const host = `${window.location.protocol}//${window.location.host}`;
    const shareLink = `${host}/NewsPage/${item?.newsItem?.id}`;
    navigator.clipboard
      .writeText(shareLink)
      .then(() => {
        toast.success(t('LinkIsCopied'), {
          theme: 'colored',
        });
      })
      .catch(() => {
        toast.error(t('LinkNotCopied'), {
          theme: 'colored',
        });
      });
  };

  const parserOptions = createNewsContentParserOptions({
    token: user?.access_token,
    stripImages: true,
    onUnavailable: () => toast.error(t('DocumentLinkUnavailable')),
    onDownload: (documentId) => {
      const documentApi = new DocumentAPI();
      documentApi.LogDownloadV2(documentId, 'web').catch(() => undefined);
    },
  });

  return (
    <>
      <Card
        sx={{
          boxShadow: 'none',
          borderRadius: 1,
          display: 'flex',
          height: { xs: null, md: imageError ? null : 300 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%',
          }}
        >
          <CardHeader
            sx={{
              backgroundColor: isParentCompany(item?.newsItem?.companyId)
                ? Colors.PETROL
                : 'primary.main',
            }}
            title={
              <Typography variant='h5' color={Colors.WHITE}>
                {item?.newsItem?.title}
              </Typography>
            }
            subheader={
              <Typography variant='subtitle1' color={Colors.WHITE}>
                {dayjs(item?.newsItem?.publishDate).format('YYYY-MM-DD')}
              </Typography>
            }
          />
          {!imageError && (
            <CardMedia
              sx={{
                display: { xs: 'block', md: 'none' },
                width: '100%',
                '&.MuiCardMedia-img': {
                  width: '100%',
                  objectFit: 'cover',
                },
              }}
              height='auto'
              image={fileUrl(
                item?.newsItem?.featuredImageId,
                user?.access_token,
              )}
              component='img'
              onError={() => {
                setImageError(true);
              }}
            />
          )}
          <CardContent className='fade-out' sx={{ py: 0 }}>
            <Typography
              variant='body2'
              component='div'
              className='fiveRowsEllipsis'
            >
              {parse(normalizedContent, parserOptions)}
            </Typography>
          </CardContent>
          <CardActions
            disableSpacing
            sx={{ display: 'flex', flexGrow: 1, alignItems: 'flex-end' }}
          >
            <Button
              variant='text'
              sx={{ color: 'primary.main' }}
              onClick={() => setModalOpen(true)}
            >
              {t('ReadMore')}...
            </Button>
            {GetAdminPermission(0, 'SuperAdmin') && (
              <Button
                variant='text'
                sx={{ color: 'primary.main', ml: 1 }}
                onClick={handleShare}
              >
                {t('Share')}
              </Button>
            )}
          </CardActions>
        </Box>
        {!imageError && (
          <CardMedia
            sx={(theme) => ({
              display: { xs: 'none', md: 'block' },
              maxWidth: 400,
              // Apply when right rail is open on typical laptop widths (>= lg).
              [theme.breakpoints.down('xl')]: {
                '[data-right-rail="open"] &': {
                  display: 'none',
                },
              },
            })}
            height='300px'
            image={fileUrl(item?.newsItem?.featuredImageId, user?.access_token)}
            component='img'
            onError={() => {
              setImageError(true);
            }}
          />
        )}
      </Card>
      {modalOpen && (
        <NewsModal
          id={item?.newsItem?.id}
          modalOpen={modalOpen}
          setModalOpen={(arg: boolean) => setModalOpen(arg)}
        />
      )}
    </>
  );
};

export default FeedNewsItem;
